import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { DayPickerInput, SingleSelect } from '~/components';
import { useDocumentTitle } from '~/hooks';
import { colors, weights } from '~/styles';
import { dateFormats } from '~/utils';
import { Header, Page } from '../../components';
import { Section, SectionTitle } from './shared';
import Summary from './Summary';
import Conversations from './Conversations';
import CostSummary from './CostSummary';
import Concentration from './Concentration';
import CostByModel from './CostByModel';
import ToolCalls from './ToolCalls';
import Members from './Members';
import Workspaces from './Workspaces';

const tabs = ['activity', 'cost', 'workspaces', 'members', 'tooling'];

const TIMESPANS = {
  today: {
    label: 'Today',
    getDates: () => ({
      start: moment.utc().format(dateFormats.isoDate),
      end: moment.utc().format(dateFormats.isoDate),
    }),
  },
  'this-week-to-date': {
    label: 'This Week to Date',
    getDates: () => ({
      start: moment.utc().startOf('isoWeek').format(dateFormats.isoDate),
      end: moment.utc().format(dateFormats.isoDate),
    }),
  },
  'this-month-to-date': {
    label: 'This Month to Date',
    getDates: () => ({
      start: moment.utc().startOf('month').format(dateFormats.isoDate),
      end: moment.utc().format(dateFormats.isoDate),
    }),
  },
  'last-7': {
    label: 'Last 7 Days',
    getDates: () => ({
      start: moment.utc().subtract(7, 'days').format(dateFormats.isoDate),
      end: moment.utc().format(dateFormats.isoDate),
    }),
  },
  'last-30': {
    label: 'Last 30 Days',
    getDates: () => ({
      start: moment.utc().subtract(30, 'days').format(dateFormats.isoDate),
      end: moment.utc().format(dateFormats.isoDate),
    }),
  },
  'last-week': {
    label: 'Last Week',
    getDates: () => ({
      start: moment.utc().startOf('isoWeek').subtract(1, 'week').format(dateFormats.isoDate),
      end: moment.utc().subtract(1, 'week').endOf('isoWeek').format(dateFormats.isoDate),
    }),
  },
  'last-month': {
    label: 'Last Month',
    getDates: () => ({
      start: moment.utc().subtract(1, 'month').startOf('month').format(dateFormats.isoDate),
      end: moment.utc().subtract(1, 'month').endOf('month').format(dateFormats.isoDate),
    }),
  },
  custom: { label: 'Custom', getDates: null },
};

const Filters = styled.div`
  display: flex;
`;

const Filter = styled.div`
  display: flex;
  align-items: center;

  &:not(:first-child) {
    margin-left: 1.5rem;
  }
`;

const TabBar = styled.ul`
  display: flex;
  margin-top: 1.5rem;
  border-bottom: solid 1px ${colors.grey10};
`;

const Tab = styled.li`
  padding: 0.75rem 1.5rem;
  font-weight: ${({ isActive }) => (isActive ? weights.bold : weights.normal)};
  color: ${({ isActive }) => (isActive ? colors.black : colors.grey50)};
  border-bottom: solid 2px ${({ isActive }) => (isActive ? colors.primary : 'transparent')};
  cursor: pointer;

  &:hover {
    color: ${colors.black};
  }
`;

const Content = styled.div`
  margin-top: 2rem;
`;

export default function AiDashboard() {
  useDocumentTitle('AI Dashboard - Admin');

  const history = useHistory();
  const route = useRouteMatch('/admin/ai/dashboard/:tab?');
  const routeTab = route?.params?.tab;
  const [tab, setTab] = useState();

  const initialParams = useRef(new URLSearchParams(window.location.search));

  const [timespan, setTimespan] = useState(() => initialParams.current.get('timespan') || 'last-30');
  const [granularity, setGranularity] = useState(() => initialParams.current.get('granularity') || 'day');
  // Resolve the initial range from the URL-provided timespan so child panels fetch the
  // correct dates on first render. Without this, a bookmarked `?timespan=today` would
  // make Summary/Conversations fire a round of requests for the hard-coded 30-day range
  // before the timespan effect re-sets start/end.
  const initialRange = useRef(TIMESPANS[initialParams.current.get('timespan') || 'last-30']?.getDates?.() ?? null);
  const [start, setStart] = useState(
    () =>
      initialParams.current.get('start') ||
      initialRange.current?.start ||
      moment.utc().subtract(30, 'days').format(dateFormats.isoDate),
  );
  const [end, setEnd] = useState(
    () => initialParams.current.get('end') || initialRange.current?.end || moment.utc().format(dateFormats.isoDate),
  );

  useEffect(() => {
    if (!routeTab || !tabs.includes(routeTab)) {
      setTab('activity');
      history.replace({ pathname: '/admin/ai/dashboard/activity', search: window.location.search });
    } else {
      setTab(routeTab);
    }
  }, [history, routeTab]);

  useEffect(() => {
    if (tab === undefined || window.location.pathname === `/admin/ai/dashboard/${tab}`) return;
    history.push({ pathname: `/admin/ai/dashboard/${tab}`, search: window.location.search });
  }, [history, tab]);

  useEffect(() => {
    const range = TIMESPANS[timespan]?.getDates?.();
    if (range) {
      setStart(range.start);
      setEnd(range.end);
    }
  }, [timespan]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('timespan', timespan);
    params.set('granularity', granularity);
    if (timespan === 'custom') {
      if (start) params.set('start', start);
      if (end) params.set('end', end);
    }
    history.replace({ search: `?${params.toString()}` });
  }, [timespan, granularity, start, end, history]);

  return (
    <Page>
      <Header />
      <Filters>
        <Filter style={{ minWidth: '12rem' }}>
          <SingleSelect placeholder="Timespan" value={timespan} onChange={(e) => setTimespan(e.target.value)}>
            {Object.entries(TIMESPANS).map(([key, { label }]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </SingleSelect>
        </Filter>
        {timespan === 'custom' && (
          <>
            <Filter>
              <DayPickerInput placeholder="Start" value={start} onChange={(value) => setStart(value ?? undefined)} />
            </Filter>
            <Filter>
              <DayPickerInput placeholder="End" value={end} onChange={(value) => setEnd(value ?? undefined)} />
            </Filter>
          </>
        )}
        {timespan !== 'custom' && (
          <Filter>
            {!!start && moment(start).format(dateFormats.compactDate)} -{' '}
            {!!end && moment(end).format(dateFormats.compactDate)}
          </Filter>
        )}
        {(tab === 'activity' || tab === 'cost') && (
          <Filter style={{ minWidth: '10rem' }}>
            <SingleSelect
              placeholder="Granularity"
              value={granularity}
              onChange={(e) => setGranularity(e.target.value)}>
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </SingleSelect>
          </Filter>
        )}
      </Filters>
      <TabBar>
        <Tab isActive={tab === 'activity'} onClick={() => setTab('activity')}>
          Activity
        </Tab>
        <Tab isActive={tab === 'cost'} onClick={() => setTab('cost')}>
          Cost
        </Tab>
        <Tab isActive={tab === 'workspaces'} onClick={() => setTab('workspaces')}>
          Workspaces
        </Tab>
        <Tab isActive={tab === 'members'} onClick={() => setTab('members')}>
          Members
        </Tab>
        <Tab isActive={tab === 'tooling'} onClick={() => setTab('tooling')}>
          Tooling
        </Tab>
      </TabBar>
      <Content>
        {tab === 'activity' && (
          <>
            <Section>
              <Summary start={start} end={end} granularity={granularity} />
            </Section>
            <Conversations start={start} end={end} granularity={granularity} />
          </>
        )}
        {tab === 'cost' && (
          <>
            <CostSummary start={start} end={end} granularity={granularity} />
            <SectionTitle>Cost Distribution</SectionTitle>
            <Section>
              <Concentration start={start} end={end} />
            </Section>
            <Section>
              <CostByModel start={start} end={end} />
            </Section>
          </>
        )}
        {tab === 'workspaces' && <Workspaces start={start} end={end} />}
        {tab === 'members' && <Members start={start} end={end} />}
        {tab === 'tooling' && <ToolCalls start={start} end={end} />}
      </Content>
    </Page>
  );
}
